import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle, AlertTriangle, MessageSquare, Star, ArrowLeft, ArrowRight, Zap, Share2, Copy, Check, Globe, ExternalLink, RefreshCw } from "lucide-react";
import { DiscussionAd } from "../components/AdBanner";
import { VoiceInput } from "../components/VoiceInput";
import { ExportMenu } from "../components/ExportMenu";
import { InlineDisclaimer } from "./DisclaimerPage";
import { CommunicationLevelToggle } from "../components/CommunicationLevelToggle";
import { SocialShareButtons } from "../components/SocialShareButtons";
import { ProDebatePanel } from "../components/ProDebatePanel";
import { SEO, articleLd, breadcrumbLd } from "../components/SEO";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PROVIDER_COLORS = { openai: "#10a37f", anthropic: "#d97706", gemini: "#4285f4", perplexity: "#20808d" };

const CONFIDENCE_COLORS = { high: "text-green-400", medium: "text-yellow-400", low: "text-red-400" };
const CLAIM_STATUS_COLORS = {
  verified: "bg-green-100 text-green-700",
  unverified: "bg-red-100 text-red-700",
  partially_verified: "bg-yellow-100 text-yellow-700",
};

const LIVE_STATUS_COLORS = {
  verified: "bg-green-100 text-green-800 border-green-300",
  outdated: "bg-orange-100 text-orange-800 border-orange-300",
  unverified: "bg-red-100 text-red-800 border-red-300",
  partially_verified: "bg-yellow-100 text-yellow-800 border-yellow-300",
};

const LIVE_RELIABILITY_COLORS = {
  high: "text-green-600",
  medium: "text-yellow-600",
  low: "text-red-600",
};

const LiveVerification = ({ discussionId, existing }) => {
  const [result, setResult] = useState(existing || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/debate/${discussionId}/verify-live`);
      const live = res.data.live_verification;
      setResult(live);
      if (!live?.available) {
        setError(live?.message || "Live verificatie niet beschikbaar.");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Live verificatie mislukt.");
    }
    setLoading(false);
  };

  // No result yet — show "run" button
  if (!result) {
    return (
      <section className="mb-8 bg-blue-50 border-2 border-blue-200 p-5 print-block" data-testid="live-verification-cta">
        <div className="flex items-start gap-3">
          <Globe className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-bold text-sm mb-1">Live internet-verificatie</h3>
            <p className="text-xs text-gray-700 mb-3">
              De AI-debaters gebruiken trainings-data die enkele jaren oud kan zijn. Check de claims tegen actuele
              internet-bronnen via Perplexity Sonar voor up-to-date verificatie.
            </p>
            <button onClick={run} disabled={loading}
              className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold disabled:opacity-50 transition-colors"
              data-testid="run-live-verification-btn"
            >
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Globe className="w-3 h-3" />}
              {loading ? "Verifieert via internet..." : "Verifieer live op internet"}
            </button>
            {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
          </div>
        </div>
      </section>
    );
  }

  // Not available (no key, failed call)
  if (!result.available) {
    return (
      <section className="mb-8 bg-gray-50 border border-gray-200 p-4 print-block" data-testid="live-verification-unavailable">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs text-gray-700">{result.message || "Live verificatie niet beschikbaar."}</p>
            <button onClick={run} disabled={loading}
              className="mt-2 text-[11px] text-blue-600 hover:underline"
              data-testid="retry-live-verification"
            >
              Opnieuw proberen
            </button>
          </div>
        </div>
      </section>
    );
  }

  // Result rendered
  return (
    <section className="mb-8 bg-white border-2 border-blue-300 p-5 print-block" data-testid="live-verification-result">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Globe className="w-5 h-5 text-blue-600" />
          <h3 className="font-bold text-sm">Live internet-verificatie</h3>
        </div>
        <button onClick={run} disabled={loading}
          className="no-print text-[11px] text-blue-600 hover:underline inline-flex items-center gap-1"
          data-testid="refresh-live-verification"
          title="Verifieer opnieuw"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Ververs
        </button>
      </div>

      <div className="space-y-3">
        {(result.verified_claims || []).map((v) => (
          <div key={`live-${v.claim}`} className="border border-gray-200 p-3">
            <div className="flex items-start gap-2 mb-2">
              <span className={`text-[10px] font-mono px-1.5 py-0.5 border ${LIVE_STATUS_COLORS[v.status] || LIVE_STATUS_COLORS.partially_verified}`}>
                {v.status?.toUpperCase()}
              </span>
              <p className="text-xs font-medium flex-1">{v.claim}</p>
            </div>
            {v.evidence && <p className="text-xs text-gray-600 mb-2">{v.evidence}</p>}
            {Array.isArray(v.sources) && v.sources.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-1">
                {v.sources.map((src, i) => (
                  <a key={`src-${v.claim}-${i}`} href={src} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[10px] text-blue-600 hover:underline truncate max-w-[280px]"
                  >
                    <ExternalLink className="w-2.5 h-2.5 flex-shrink-0" />
                    {src.replace(/^https?:\/\//, "").split("/")[0]}
                  </a>
                ))}
              </div>
            )}
            {v.last_checked && <p className="text-[10px] text-gray-400 mt-1">Gecheckt: {v.last_checked}</p>}
          </div>
        ))}
      </div>

      <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between">
        <span className="text-xs font-mono text-gray-500">
          Live betrouwbaarheid:{" "}
          <span className={`font-bold ${LIVE_RELIABILITY_COLORS[result.overall_reliability] || "text-gray-500"}`}>
            {result.overall_reliability}
          </span>
        </span>
        <span className="text-[10px] text-gray-400">via Perplexity Sonar · {result.checked_at}</span>
      </div>
    </section>
  );
};

const ShareButton = ({ discussionId, question }) => {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/discussion/${discussionId}`;
  
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: `AI Debate: ${question}`, url: shareUrl });
        return;
      } catch (err) {
        // User cancelde de native share-dialog of share is niet ondersteund — val terug op clipboard
        if (process.env.NODE_ENV !== "production") console.debug("[Share] native share failed:", err?.message);
      }
    }
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button onClick={handleShare}
      className="inline-flex items-center gap-2 px-4 py-2 border border-[#E5E5E5] text-sm font-bold text-[#52525B] hover:border-[#2d8cf0] hover:text-[#2d8cf0] transition-colors"
      data-testid="share-debate-btn"
    >
      {copied ? <Check className="w-4 h-4 text-green-600" /> : <Share2 className="w-4 h-4" />}
      {copied ? "Link gekopieerd!" : "Delen"}
    </button>
  );
};
const FUNC_GROUP_COLORS = {
  conflict: "#DC2626", evidence: "#2563EB", strategy: "#7C3AED",
  execution: "#059669", human: "#D97706", oversight: "#BE185D",
};

const ResponseCard = ({ response, debaters }) => {
  const color = PROVIDER_COLORS[response.provider] || "#666";
  const debater = debaters[response.role] || {};
  return (
    <div className="bg-white border-2 border-gray-200 p-5" data-testid={`response-${response.role}`}>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="text-xs font-mono font-bold">{response.role_display}</span>
        <span className="text-[10px] font-mono text-gray-400">via</span>
        <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 bg-gray-100" style={{ color }}>{response.provider_display}</span>
      </div>
      {debater.groups && (
        <div className="flex gap-1 mb-2">
          {debater.groups.map(g => (
            <span key={g} className="text-[9px] font-mono uppercase px-1 py-0.5" style={{ backgroundColor: `${FUNC_GROUP_COLORS[g]}15`, color: FUNC_GROUP_COLORS[g] }}>{g}</span>
          ))}
        </div>
      )}
      {response.error ? (
        <p className="text-red-500 text-sm"><AlertTriangle className="w-3 h-3 inline mr-1" />{response.error}</p>
      ) : (
        <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{response.response}</div>
      )}
    </div>
  );
};

const FeedbackWidget = ({ discussionId }) => {
  const [score, setScore] = useState(0);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const submit = async () => {
    if (score === 0) return;
    try {
      await axios.post(`${API}/feedback`, { discussion_id: discussionId, score, comment });
      setSubmitted(true);
      toast.success("Bedankt voor je feedback!");
    } catch { toast.error("Feedback opslaan mislukt"); }
  };

  if (submitted) return (
    <div className="bg-green-50 border-2 border-green-200 p-4 text-center" data-testid="feedback-submitted">
      <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
      <p className="text-sm text-green-700 font-medium">Bedankt voor je feedback!</p>
    </div>
  );

  return (
    <div className="bg-white border-2 border-gray-200 p-5" data-testid="feedback-widget">
      <p className="text-xs font-mono uppercase text-gray-500 mb-3">Hoe nuttig was dit debat?</p>
      <div className="flex items-center gap-1 mb-3">
        {[1, 2, 3, 4, 5].map(s => (
          <button key={s} onClick={() => setScore(s)} data-testid={`rating-${s}`}
            className={`w-9 h-9 flex items-center justify-center transition-colors ${
              s <= score ? "bg-yellow-400 text-white" : "bg-gray-100 text-gray-400 hover:bg-gray-200"
            }`}
          >
            <Star className="w-4 h-4" fill={s <= score ? "white" : "none"} />
          </button>
        ))}
        <span className="text-xs text-gray-400 ml-2">{score > 0 ? `${score}/5` : ""}</span>
      </div>
      <div className="flex gap-2">
        <VoiceInput
          onTranscript={(text) => setComment(prev => (prev ? prev + " " : "") + text)}
          size="sm"
          title="Voeg een opmerking toe via spraak"
        />
        <input value={comment} onChange={e => setComment(e.target.value)}
          placeholder="Opmerkingen (optioneel)..."
          className="flex-1 text-xs border border-gray-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]"
        />
        <button onClick={submit} disabled={score === 0}
          className="px-3 py-2 bg-gray-900 text-white text-xs font-bold hover:bg-[#002FA7] disabled:opacity-50 transition-colors"
          data-testid="submit-feedback-btn"
        >
          Verstuur
        </button>
      </div>
    </div>
  );
};

export const DiscussionPage = () => {
  const { id } = useParams();
  const [discussion, setDiscussion] = useState(null);
  const [debaters, setDebaters] = useState({});
  const [loading, setLoading] = useState(true);
  const [humanInput, setHumanInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Continue-the-thread state (see user proposal DE-20260705-423820 continue flow)
  const [followUpDraft, setFollowUpDraft] = useState(null); // string | null — when set, renders the continue panel
  const [continuing, setContinuing] = useState(false);
  const continuePanelRef = useRef(null);
  const synthesisRef = useRef(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/discussions/${id}`),
      axios.get(`${API}/roles`)
    ]).then(([d, r]) => {
      setDiscussion(d.data);
      setDebaters(r.data.roles || {});
      setLoading(false);
    }).catch(() => { toast.error("Discussie laden mislukt"); setLoading(false); });
  }, [id]);

  const submitRound2 = async () => {
    if (!humanInput.trim()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/debate/round2`, { discussion_id: id, human_input: humanInput });
      setDiscussion(prev => ({ ...prev, ...res.data, round2: res.data.round2_responses, synthesis: res.data.synthesis, verification: res.data.verification, status: "completed" }));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "llm_budget_exceeded") {
        toast.error("AI-budget is tijdelijk uitgeput. Probeer het later opnieuw.", { duration: 6000 });
      } else {
        toast.error("Ronde 2 mislukt");
      }
    }
    finally { setSubmitting(false); }
  };

  const finalizeWithoutRound2 = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/debate/${id}/finalize`);
      setDiscussion(prev => ({ ...prev, synthesis: res.data.synthesis, verification: res.data.verification, status: "completed", finalized_without_round2: true }));
      toast.success("Debat afgerond — synthese en verificatie gegenereerd.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "llm_budget_exceeded") {
        toast.error("AI-budget is tijdelijk uitgeput. Probeer het later opnieuw.", { duration: 6000 });
      } else {
        toast.error("Afronden mislukt");
      }
    }
    finally { setSubmitting(false); }
  };

  const openContinuePanel = (question) => {
    setFollowUpDraft(question);
    // Scroll into view after render
    setTimeout(() => {
      continuePanelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
  };

  const submitContinue = async () => {
    const q = (followUpDraft || "").trim();
    if (!q) return;
    setContinuing(true);
    try {
      const res = await axios.post(`${API}/debate/${id}/continue`, { follow_up_question: q });
      setDiscussion(prev => ({
        ...prev,
        continuations: [...(prev.continuations || []), res.data.new_continuation],
        synthesis: res.data.synthesis,
        verification: res.data.verification,
        // Invalidate stale wrap-up artefacts locally — they were computed on the old synthesis
        live_verification: undefined,
        cross_examination: undefined,
        arbiter_report: undefined,
      }));
      setFollowUpDraft(null);
      toast.success(`Vervolgronde ${res.data.continuation_depth} van ${res.data.max_continuations} — team analyseert de nieuwe vraag.`);
      // Scroll to the (new) synthesis so the user sees the updated summary right away
      setTimeout(() => {
        synthesisRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "max_continuations_reached") {
        toast.error(detail.message, { duration: 8000 });
      } else if (detail?.code === "llm_budget_exceeded") {
        toast.error("AI-budget is tijdelijk uitgeput. Probeer het later opnieuw.", { duration: 6000 });
      } else {
        toast.error("Vervolgronde mislukt");
      }
    }
    finally { setContinuing(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>;
  if (!discussion) return <div className="text-center py-12 text-gray-500">Discussie niet gevonden</div>;

  const seoDescription = (discussion.synthesis?.work_conclusion || discussion.question || "Multi-perspectief AI debat").toString().slice(0, 200);
  const isCompleted = discussion.status === "completed";

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 print-container" data-testid="discussion-page">
      <SEO
        title={discussion.question || "AI Debat"}
        description={seoDescription}
        path={`/discussion/${id}`}
        type="article"
        noindex={!isCompleted}
        jsonLd={isCompleted ? articleLd({
          headline: discussion.question,
          description: seoDescription,
          datePublished: discussion.created_at,
          url: `https://debate-engine.com/discussion/${id}`,
        }) : breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Debat", path: `/discussion/${id}` },
        ])}
      />
      <Link to="/" className="inline-flex items-center gap-1 text-xs font-mono text-gray-400 hover:text-[#002FA7] mb-4 transition-colors no-print">
        <ArrowLeft className="w-3 h-3" /> Terug naar debat
      </Link>

      {/* Print-only header */}
      <div className="print-header">
        <h1>{discussion.question}</h1>
        <div className="meta">
          Debate Engine · {discussion.debater_count || discussion.round1?.length || 0} debaters · Status: {discussion.status}
          {discussion.created_at && ` · ${new Date(discussion.created_at).toLocaleDateString("nl-NL")}`}
        </div>
      </div>

      <div className="flex items-start justify-between gap-4 mb-2">
        <h1 className="text-xl sm:text-2xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }} data-testid="discussion-question">
          {discussion.question}
        </h1>
        <ExportMenu discussion={discussion} className="flex-shrink-0" />
      </div>
      <p className="text-xs font-mono text-gray-400 mb-6 no-print">{discussion.debater_count || discussion.round1?.length || 0} debaters | {discussion.status}</p>

      {/* Round 1 */}
      <section className="mb-4">
        <h2 className="text-xs font-mono uppercase text-gray-500 mb-3" data-testid="round1-header">Ronde 1 — Eerste Analyse</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(discussion.round1 || []).map((r) => <ResponseCard key={`r1-${r.role}-${r.provider}`} response={r} debaters={debaters} />)}
        </div>
      </section>

      {/* Ad Position 1: Between Round 1 and Human Input */}
      <div className="no-print"><DiscussionAd variant="between" /></div>

      {/* Human Input — Invite user interaction */}
      {discussion.status === "round1_complete" && (
        <section className="mb-8 bg-white border-2 border-[#002FA7] p-6" data-testid="human-input-section">
          <h2 className="text-xs font-mono uppercase text-[#002FA7] mb-2">Jouw Inbreng</h2>
          <p className="text-sm text-gray-600 mb-3">
            Daag de conclusies uit, stel vervolgvragen, voeg context toe, of geef aan waar je het mee eens/oneens bent.
          </p>
          <div className="flex gap-3">
            <textarea value={humanInput} onChange={e => setHumanInput(e.target.value)}
              placeholder="Deel je gedachten, stel vragen, of daag de experts uit..."
              className="flex-1 bg-gray-50 border-2 border-gray-200 px-4 py-3 text-sm focus:border-[#002FA7] focus:outline-none resize-none h-24"
              data-testid="human-input-textarea"
            />
            <button onClick={submitRound2} disabled={submitting || !humanInput.trim()}
              className="bg-[#002FA7] text-white px-6 font-bold text-sm hover:bg-blue-800 transition-colors disabled:opacity-50 self-end flex items-center gap-2"
              data-testid="submit-round2-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              RONDE 2
            </button>
          </div>
          {submitting && (
            <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
              <Loader2 className="w-3 h-3 animate-spin" /> Debaters analyseren je input...
            </div>
          )}

          {/* Skip Round 2: finalize directly with synthesis + verification + arbiter */}
          <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-gray-500 flex-1 min-w-[240px]">
              Geen vervolgvraag? Rond direct af — je krijgt dan de synthese, live-verificatie, kruisverhoor en arbiter op basis van Ronde 1.
            </p>
            <button
              onClick={finalizeWithoutRound2}
              disabled={submitting}
              className="border-2 border-gray-300 text-gray-700 hover:border-[#002FA7] hover:text-[#002FA7] transition-colors px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2 disabled:opacity-50"
              data-testid="finalize-without-round2-btn"
            >
              {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
              Rond direct af
            </button>
          </div>
        </section>
      )}

      {/* Round 2 */}
      {discussion.round2 && (
        <section className="mb-8">
          <h2 className="text-xs font-mono uppercase text-gray-500 mb-3" data-testid="round2-header">Ronde 2 — Verdieping</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {discussion.round2.map((r) => <ResponseCard key={`r2-${r.role}-${r.provider}`} response={r} debaters={debaters} />)}
          </div>
        </section>
      )}

      {/* Synthesis moved BELOW continuations (rendered later) so latest state
          is always adjacent to the newest continuation. See v1.15.1. */}

      {/* Continuations — additional rounds triggered via follow-up-question clicks */}
      {(discussion.continuations || []).map((cont, idx) => (
        <section key={`cont-${idx}`} className="mb-8" data-testid={`continuation-round-${idx + 1}`}>
          <div className="flex items-baseline gap-2 mb-3 flex-wrap">
            <h2 className="text-xs font-mono uppercase text-gray-500">Vervolgronde {idx + 1}</h2>
            <span className="text-[10px] font-mono text-gray-400">op vervolgvraag:</span>
          </div>
          <div className="bg-blue-50 border-l-4 border-[#002FA7] px-4 py-2 mb-4">
            <p className="text-sm text-gray-800 italic">&ldquo;{cont.question}&rdquo;</p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {cont.responses?.map((r) => (
              <ResponseCard key={`${idx}-${r.role}`} response={r} debaters={debaters} />
            ))}
          </div>
        </section>
      ))}

      {/* Synthesis — always reflects the latest state (rerenders after continuations) */}
      {discussion.synthesis && (
        <section
          ref={synthesisRef}
          className="mb-8 bg-gray-900 text-white p-6 print-block"
          data-testid="synthesis-section"
        >
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <h2 className="text-xs font-mono uppercase text-blue-400">Synthese</h2>
            <CommunicationLevelToggle
              discussionId={id}
              originalSynthesis={discussion.synthesis}
              onChange={(next) => setDiscussion((prev) => ({ ...prev, synthesis: next }))}
            />
          </div>
          {discussion.synthesis.work_conclusion && <p className="text-sm mb-4 leading-relaxed">{discussion.synthesis.work_conclusion}</p>}
          {discussion.synthesis.strongest_surviving_claims?.length > 0 && (
            <div className="mb-3">
              <span className="text-[10px] font-mono text-gray-400 block mb-1">STERKSTE CLAIMS</span>
              {discussion.synthesis.strongest_surviving_claims.map((c) => (
                <div key={`strong-${c}`} className="flex items-start gap-2 mb-1"><CheckCircle className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" /><span className="text-xs">{c}</span></div>
              ))}
            </div>
          )}
          {discussion.synthesis.weakest_claims?.length > 0 && (
            <div className="mb-3">
              <span className="text-[10px] font-mono text-gray-400 block mb-1">ZWAKSTE CLAIMS</span>
              {discussion.synthesis.weakest_claims.map((c) => (
                <div key={`weak-${c}`} className="flex items-start gap-2 mb-1"><AlertTriangle className="w-3 h-3 text-red-400 mt-0.5 flex-shrink-0" /><span className="text-xs">{c}</span></div>
              ))}
            </div>
          )}
          {discussion.synthesis.key_tensions?.length > 0 && (
            <div className="mb-3">
              <span className="text-[10px] font-mono text-gray-400 block mb-1">KERNSPANNINGEN</span>
              {discussion.synthesis.key_tensions.map((t) => (
                <div key={`tension-${t}`} className="text-xs text-gray-300 mb-1">- {t}</div>
              ))}
            </div>
          )}
          {discussion.synthesis.new_insight && (
            <div className="mt-3 border-t border-gray-700 pt-3">
              <span className="text-[10px] font-mono text-gray-400 block mb-1">NIEUW INZICHT</span>
              <p className="text-sm">{discussion.synthesis.new_insight}</p>
            </div>
          )}
          {discussion.synthesis.further_questions?.length > 0 && (
            <div className="mt-3 border-t border-gray-700 pt-3">
              <span className="text-[10px] font-mono text-gray-400 block mb-1">VERVOLGVRAGEN</span>
              <p className="text-[10px] text-gray-500 mb-2 no-print">Klik op de vraag voor het vervolg van de discussie en geef eventueel toegevoegde informatie.</p>
              <div className="flex flex-col gap-1.5">
                {discussion.synthesis.further_questions.map((q) => (
                  <button
                    key={`q-${q}`}
                    type="button"
                    onClick={() => openContinuePanel(q)}
                    className="group text-left text-xs text-blue-300 hover:text-white hover:bg-blue-500/20 border border-transparent hover:border-blue-400/50 px-2 py-1.5 transition-colors flex items-start gap-2"
                    data-testid={`follow-up-question-btn`}
                  >
                    <ArrowRight className="w-3 h-3 flex-shrink-0 mt-0.5 opacity-60 group-hover:opacity-100 transition-opacity" />
                    <span className="leading-relaxed">{q}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {discussion.synthesis.confidence_level && (
            <div className="mt-3 text-[10px] font-mono text-gray-500">
              Betrouwbaarheid: <span className={`font-bold ${CONFIDENCE_COLORS[discussion.synthesis.confidence_level] || "text-red-400"}`}>{discussion.synthesis.confidence_level}</span>
            </div>
          )}
        </section>
      )}

      {/* Continue panel — pre-filled editable follow-up question */}
      {followUpDraft !== null && (
        <section
          ref={continuePanelRef}
          className="mb-8 bg-white border-2 border-blue-400 p-6 shadow-sm"
          data-testid="continue-panel"
        >
          <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
            <div>
              <h2 className="text-xs font-mono uppercase text-blue-700 mb-1">Vervolg deze thread</h2>
              <p className="text-xs text-gray-600">
                Hetzelfde team, alle context van eerder. Verfijn eventueel de vraag of voeg toegevoegde informatie toe, en druk op &quot;Stel deze vraag aan het team&quot;.
              </p>
            </div>
            <span className="text-[10px] font-mono text-gray-400">
              vervolgronde {(discussion.continuations?.length || 0) + 1} van 5
            </span>
          </div>
          <textarea
            value={followUpDraft}
            onChange={(e) => setFollowUpDraft(e.target.value)}
            disabled={continuing}
            className="w-full bg-gray-50 border-2 border-gray-200 px-4 py-3 text-sm focus:border-blue-500 focus:outline-none resize-y min-h-[80px]"
            data-testid="continue-panel-textarea"
            placeholder="Bewerk de vervolgvraag desgewenst..."
          />
          <div className="flex gap-2 mt-3 flex-wrap">
            <button
              onClick={submitContinue}
              disabled={continuing || !followUpDraft.trim()}
              className="bg-[#002FA7] text-white px-5 py-2 font-bold text-sm hover:bg-blue-800 transition-colors disabled:opacity-50 flex items-center gap-2"
              data-testid="continue-panel-submit"
            >
              {continuing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Stel deze vraag aan het team
            </button>
            <button
              onClick={() => setFollowUpDraft(null)}
              disabled={continuing}
              className="border border-gray-300 text-gray-600 px-4 py-2 text-sm hover:border-gray-500 transition-colors disabled:opacity-50"
              data-testid="continue-panel-cancel"
            >
              Annuleer
            </button>
          </div>
          {continuing && (
            <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
              <Loader2 className="w-3 h-3 animate-spin" /> Team analyseert de nieuwe vraag met volledige context...
            </div>
          )}
        </section>
      )}
      {discussion.verification?.verified_claims && (
        <section className="mb-8 print-block" data-testid="verification-section">
          <h2 className="text-xs font-mono uppercase text-gray-500 mb-3">Verificatie</h2>
          <div className="bg-white border-2 border-gray-200 p-5">
            {discussion.verification.verified_claims.map((v) => (
              <div key={`vc-${v.claim}`} className="flex items-start gap-3 mb-3 last:mb-0">
                <span className={`text-[10px] font-mono px-1.5 py-0.5 mt-0.5 flex-shrink-0 ${CLAIM_STATUS_COLORS[v.status] || CLAIM_STATUS_COLORS.partially_verified}`}>{v.status}</span>
                <div><p className="text-sm font-medium">{v.claim}</p>{v.evidence && <p className="text-xs text-gray-500 mt-1">{v.evidence}</p>}</div>
              </div>
            ))}
            <div className="mt-3 pt-3 border-t border-gray-200 text-xs font-mono text-gray-500">
              Betrouwbaarheid: <span className="font-bold">{discussion.verification.overall_reliability}</span>
            </div>
          </div>
        </section>
      )}

      {/* Live Internet Verification — only after debate is complete with synthesis */}
      {discussion.status === "completed" && discussion.synthesis?.strongest_surviving_claims?.length > 0 && (
        <LiveVerification discussionId={id} existing={discussion.live_verification} />
      )}

      {/* Pro Debate Protocol — Cross-Examination (free) + Arbiter (premium) */}
      {discussion.status === "completed" && discussion.synthesis && (
        <ProDebatePanel discussionId={id} debatersMeta={debaters} isPremium={false} />
      )}

      {/* AI Disclaimer — only after debate has output (synthesis or verification) */}
      {(discussion.synthesis || discussion.verification?.verified_claims) && <InlineDisclaimer />}

      {/* Feedback Widget — only show after debate is complete */}
      {discussion.status === "completed" && (
        <section className="mb-4">
          <FeedbackWidget discussionId={id} />
        </section>
      )}

      {/* Premium Value Proposition — soft upsell after good experience */}
      {discussion.status === "completed" && (
        <div className="mb-4 bg-gradient-to-r from-[#0a0e1a] to-[#1a2744] text-white p-5 no-print" data-testid="premium-upsell">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-bold mb-1">Dit debat gebruikte {discussion.debater_count || discussion.round1?.length || 0} experts</p>
              <p className="text-xs text-gray-400">Met Premium krijg je 6+ experts per debat, betere modellen, en geen advertenties.</p>
            </div>
            <Link to="/premium" className="px-4 py-2 bg-[#f59e0b] text-[#0a0e1a] text-xs font-bold hover:bg-[#d97706] transition-colors flex-shrink-0" data-testid="upsell-premium-btn">
              BEKIJK PREMIUM
            </Link>
          </div>
        </div>
      )}

      {/* Ad Position 2: After complete debate */}
      {discussion.status === "completed" && <div className="no-print"><DiscussionAd variant="between" /></div>}

      {/* Engagement prompt + Share */}
      {discussion.status === "completed" && (
        <div className="bg-white border border-[#E5E5E5] p-5 flex flex-col gap-4" data-testid="engagement-prompt">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex gap-3">
              <Link to="/" className="inline-flex items-center gap-2 px-4 py-2 bg-[#0a0e1a] text-white text-sm font-bold hover:bg-[#2d8cf0] transition-colors" data-testid="new-debate-btn">
                <Zap className="w-4 h-4" /> Nieuw Debat
              </Link>
              <ShareButton discussionId={id} question={discussion.question} />
            </div>
            <p className="text-xs text-[#52525B]">Deel dit debat met je netwerk</p>
          </div>
          <div className="pt-3 border-t border-gray-100">
            <SocialShareButtons
              url={`${window.location.origin}/discussion/${id}`}
              title={`AI Debate: ${discussion.question}`}
            />
          </div>
        </div>
      )}
    </div>
  );
};
